import React, { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { login } from "@services/auth-crud";
import {Link} from "react-router-dom";
import {Button, Modal} from "react-bootstrap";
import { useAuthStore } from "@stores/auth";
import { syncServerTime } from "@utils/date-utils";

/*
  INTL (i18n) docs:
  https://github.com/formatjs/react-intl/blob/master/docs/Components.md#formattedmessage
*/

/*
  Formik+YUP:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
*/

// Cambiar esta versión con cada despliegue para que el modal aparezca de nuevo
const DEPLOY_VERSION = "2026-06-13-v1";
const DEPLOY_ACCEPTED_KEY = "deploy_notice_accepted";

// Ventana de visibilidad del aviso (YYYY-MM-DD, zona horaria local, inclusivo en ambos extremos).
// Para mostrar solo un día, dejar FROM === UNTIL. Fuera de esta ventana el modal no aparece.
const DEPLOY_SHOW_FROM = "2026-06-12";
const DEPLOY_SHOW_UNTIL = "2026-06-13";

const getLocalDateISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const initialValues = {
  userName: "",
  password: "",
};

function Login() {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeployNotice, setShowDeployNotice] = useState(false);
  const isMountedRef = useRef(true);
  
  // Usar Zustand para manejo de estado
  const { setAuthToken } = useAuthStore();

  // Cleanup para evitar memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Mostrar aviso de despliegue solo dentro de la ventana de fechas y si el usuario no lo ha aceptado
  useEffect(() => {
    const today = getLocalDateISO();
    const withinWindow = today >= DEPLOY_SHOW_FROM && today <= DEPLOY_SHOW_UNTIL;
    if (!withinWindow) return;

    const accepted = localStorage.getItem(DEPLOY_ACCEPTED_KEY);
    if (accepted !== DEPLOY_VERSION) {
      setShowDeployNotice(true);
    }
  }, []);

  const handleAcceptDeployNotice = () => {
    localStorage.setItem(DEPLOY_ACCEPTED_KEY, DEPLOY_VERSION);
    setShowDeployNotice(false);
  };
  const LoginSchema = Yup.object().shape({
    userName: Yup.string()
      .min(3, "Minimo 3 caracteres")
      .max(50, "Maximo 50 caracteres")
      .required("El campo es requerido"),
    password: Yup.string()
      .min(3, "Minimo 3 caracteres")
      .max(50, "Maximo 50 caracteres")
      .required("El campo es requerido"),
  });


  const handleShowModal = () => {
    if (isMountedRef.current) setShowModal(true);
  };
  const handleCloseModal = () => {
    if (isMountedRef.current) setShowModal(false);
  };

  const enableLoading = () => {
    if (isMountedRef.current) setLoading(true);
  };

  const disableLoading = () => {
    if (isMountedRef.current) setLoading(false);
  };

  const getInputClasses = (fieldname) => {
    if (formik.touched[fieldname] && formik.errors[fieldname]) {
      return "is-invalid";
    }

    if (formik.touched[fieldname] && !formik.errors[fieldname]) {
      return "is-valid";
    }

    return "";
  };

  const formik = useFormik({
    initialValues,
    validationSchema: LoginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (!isMountedRef.current) return;

      enableLoading();
      try {
        const { data } = await login(values.userName, values.password);
        const { token, timestamp } = data;

        if (timestamp) {
          syncServerTime(timestamp);
        }

        await setAuthToken(token);

        if (isMountedRef.current) {
          disableLoading();
          setSubmitting(false);
        }
      } catch (err) {
        if (isMountedRef.current) {
          disableLoading();
          setSubmitting(false);
          setStatus(err.response?.data?.message || 'Ocurrio algun error');
        }
      }
    },
  });

  return (
    <div className="login-form login-signin" id="kt_login_signin_form">
      <div className="text-center mb-8">
        <img alt="AXA" src="/media/logos/AXA_Logo.svg.png" style={{ height: "64px" }} />
        <p className="text-muted font-weight-bold mt-4 mb-0">ATC Plus AXA – Reportes Hospitalarios</p>
      </div>
      {/* begin::Head */}
      <div className="text-center mb-10 mb-lg-20">
        <h3 className="font-size-h1">
          Iniciar Sesion
        </h3>
        <p className="text-muted font-weight-bold">
          Ingresa tu usuario y password.
        </p>
      </div>
      {/* end::Head */}

      {/*begin::Form*/}
      <form
          onSubmit={formik.handleSubmit}
          className="form fv-plugins-bootstrap fv-plugins-framework"
      >
        {formik.status ? (
            <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
              <div className="alert-text font-weight-bold">{formik.status}</div>
            </div>
        ) : (
            <div className="mb-10 alert alert-custom alert-light-info alert-dismissible">
              <div className="alert-text ">
                Usa tu cuenta para continuar.
              </div>
            </div>
        )}

        <div className="form-group fv-plugins-icon-container">
          <input
              placeholder="Nombre de usuario"
              type="userName"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
                  "userName"
              )}`}
              name="userName"
              {...formik.getFieldProps("userName")}
          />
          {formik.touched.userName && formik.errors.userName ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.userName}</div>
              </div>
          ) : null}
        </div>
        <div className="form-group fv-plugins-icon-container">
          <input
              placeholder="Password"
              type="password"
              className={`form-control form-control-solid h-auto py-5 px-6 ${getInputClasses(
                  "password"
              )}`}
              name="password"
              {...formik.getFieldProps("password")}
          />
          {formik.touched.password && formik.errors.password ? (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.password}</div>
              </div>
          ) : null}
        </div>

        <div className="form-group fv-plugins-icon-container">
          <div>
            <a href="#!" className="text-muted" onClick={handleShowModal}>
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>
        <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
          <button
              id="kt_login_signin_submit"
              type="submit"
              disabled={formik.isSubmitting}
              className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}
          >
            <span>Ingresar</span>
            {loading && <span className="ml-3 spinner spinner-white"></span>}
          </button>
        </div>
      </form>
      {/*end::Form*/}

      {/*Modal forgot password*/}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Recuperar Contraseña</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Contenido del modal, puede ser un formulario para recuperar la contraseña */}
          <p>Su cuenta ha sido bloqueada por seguridad ingrese su email y recibirá un correo de restablecimiento.</p>
          <input type="email" placeholder="Email" className="form-control" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleCloseModal}>
            Enviar
          </Button>
        </Modal.Footer>
      </Modal>

      {/*Modal aviso de despliegue*/}
      <Modal show={showDeployNotice} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title>Nueva actualización disponible</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Se ha realizado una actualización del sistema. Para evitar errores de visualización es necesario <strong>limpiar la caché y cookies</strong> de tu navegador.</p>

          <div className="alert alert-info mt-3" style={{ fontSize: "13px" }}>
            <strong>Google Chrome:</strong>
            <ol className="mb-0 mt-1">
              <li>Presiona <strong>Ctrl + Shift + Delete</strong> (Windows) o <strong>Cmd + Shift + Delete</strong> (Mac)</li>
              <li>Selecciona <strong>"Desde siempre"</strong> en el rango de tiempo</li>
              <li>Marca <strong>"Cookies y datos de sitios"</strong> e <strong>"Imágenes y archivos en caché"</strong></li>
              <li>Haz clic en <strong>"Borrar datos"</strong></li>
              <li>Cierra y vuelve a abrir el navegador</li>
            </ol>
          </div>

          <div className="alert alert-secondary" style={{ fontSize: "13px" }}>
            <strong>Atajo rápido:</strong> También puedes recargar sin caché con <strong>Ctrl + Shift + R</strong> (Windows) o <strong>Cmd + Shift + R</strong> (Mac)
          </div>

          <p className="text-muted" style={{ fontSize: "12px" }}>Versión: {DEPLOY_VERSION}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleAcceptDeployNotice}>
            Entendido, ya limpié mi caché
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Login;
